import { ReferenceMonth } from 'bold-ui'
import {
  RastreioCancerPerguntaDbEnum,
  RastreioCancerPerguntaOpcaoEnum,
  UltimoExameCitopatologicoQuery,
} from 'graphql/types.generated'
import {
  afterBirthdate,
  beforeEqualTo,
  createValidator,
  minLength,
  referenceMonthAfterBirthDate,
  referenceMonthBeforeOrEqualToday,
  required,
} from 'util/validation'

import { CitopatologicoPerguntaField, RequisicaoExameCitopatologicoFormValues } from './model-citopatologico'

export const citopatologicoAnamneseValidator = (
  dataNascimento: string,
  serverTime: Date,
  ultimoExame?: UltimoExameCitopatologicoQuery['ultimaSolicitacaoCitopatologicoUtero']
) =>
  createValidator<RequisicaoExameCitopatologicoFormValues>(
    {
      PERGUNTA_CITO_01_01: [required],
      PERGUNTA_CITO_01_03: [required],
      PERGUNTA_CITO_01_07: [required],
      PERGUNTA_CITO_01_08: [required],
      PERGUNTA_CITO_01_11: [required],
      PERGUNTA_CITO_01_12: [required],
    },
    (values, errors) => {
      if (!values) return errors

      const examePreventivo = values[CitopatologicoPerguntaField.examePreventivo]
      const dataPreventivo = (values[CitopatologicoPerguntaField.dataPreventivo] as unknown) as
        | ReferenceMonth
        | null
        | undefined
      const dum = values[CitopatologicoPerguntaField.dum] as string

      if (examePreventivo === RastreioCancerPerguntaOpcaoEnum.SIM_PAPANICOLAU) {
        errors[CitopatologicoPerguntaField.dataPreventivo] = required(dataPreventivo)
      }

      if (dataPreventivo) {
        errors[CitopatologicoPerguntaField.dataPreventivo] =
          referenceMonthAfterBirthDate(dataPreventivo, dataNascimento) ??
          referenceMonthBeforeOrEqualToday(dataPreventivo, serverTime)
      }

      if (!values.checkDum) {
        errors[CitopatologicoPerguntaField.dum] = required(values[CitopatologicoPerguntaField.dum])
      }

      if (values[CitopatologicoPerguntaField.dum]) {
        errors[CitopatologicoPerguntaField.dum] =
          afterBirthdate(dum, dataNascimento) ?? beforeEqualTo(dum, serverTime.toDateString(), 'data atual')
      }

      const motivoExame = values[CitopatologicoPerguntaField.motivoExame]
      if (motivoExame === RastreioCancerPerguntaOpcaoEnum.RASTREAMENTO && ultimoExame) {
        const respostas = ultimoExame.respostas
        const motivoUltimo = respostas?.find((r) => r?.pergunta === RastreioCancerPerguntaDbEnum.PERGUNTA_CITO_01_01)
          ?.opcao
        const situacaoEspecialUltimo = respostas?.find(
          (r) => r?.pergunta === RastreioCancerPerguntaDbEnum.PERGUNTA_CITO_01_02
        )?.opcao
        const dataColetaUltimo = respostas?.find(
          (r) => r?.pergunta === RastreioCancerPerguntaDbEnum.PERGUNTA_CITO_03_01
        )?.data

        const imunossupressao =
          situacaoEspecialUltimo === RastreioCancerPerguntaOpcaoEnum.PESSOA_VIVENDO_COM_HIV ||
          situacaoEspecialUltimo === RastreioCancerPerguntaOpcaoEnum.OUTRAS_SITUACOES_IMUNOSSUPRESSAO

        if (
          motivoUltimo === RastreioCancerPerguntaOpcaoEnum.RASTREAMENTO &&
          imunossupressao &&
          dataColetaUltimo != null
        ) {
          const tresAnosAtras = new Date(serverTime)
          tresAnosAtras.setFullYear(tresAnosAtras.getFullYear() - 3)
          if (new Date(dataColetaUltimo) > tresAnosAtras) {
            errors[CitopatologicoPerguntaField.motivoExame] = 'Última coleta realizada há menos de 3 anos.'
          }
        }
      }

      return errors
    }
  )

export const citopatologicoExameClinicoValidator = createValidator<RequisicaoExameCitopatologicoFormValues>({
  PERGUNTA_CITO_02_01: [required],
  PERGUNTA_CITO_02_02: [required],
})

export const citopatologicoDadosDaColetaValidator = (dataNascimento: string, today: Date) =>
  createValidator<RequisicaoExameCitopatologicoFormValues>(
    {
      PERGUNTA_CITO_03_01: [required],
      PERGUNTA_CITO_03_02: [required],
      PERGUNTA_CITO_03_03: [minLength(15), required],
      PERGUNTA_CITO_03_04: [required],
      PERGUNTA_CITO_03_05: [required],
    },
    (values, errors) => {
      if (!values) return errors

      const dataDaColeta = values[CitopatologicoPerguntaField.dataDaColeta] as string

      if (dataDaColeta) {
        errors[CitopatologicoPerguntaField.dataDaColeta] =
          afterBirthdate(dataDaColeta, dataNascimento) ??
          beforeEqualTo(dataDaColeta, today.toDateString(), 'data atual')
      }

      return errors
    }
  )
